/** Environment presets with display label and single-word shortname (max 14 chars). */
export const TENANT_ENVIRONMENTS = [
  { value: 'development', label: 'Dev environment', shortName: 'dev' },
  { value: 'test', label: 'Test environment', shortName: 'test' },
  { value: 'stress', label: 'Stress environment', shortName: 'stress' },
  { value: 'release', label: 'Release environment', shortName: 'release' },
  { value: 'staging', label: 'Staging environment', shortName: 'staging' },
  { value: 'nonprod', label: 'Non-prod environment', shortName: 'nonprod' },
  { value: 'production', label: 'Prod environment', shortName: 'prod' },
  { value: 'custom', label: 'Custom name', shortName: '' }
];

export const ENV_SHORT_NAME_MAX = 14;

/**
 * Sanitize to a single-word shortname: lowercase, alphanumeric only, max length.
 */
export const toEnvironmentShortName = (raw, maxLen = ENV_SHORT_NAME_MAX) => {
  const word = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .slice(0, maxLen);
  return word;
};

export const getEnvironmentShortName = (environment, customName = '') => {
  if (environment === 'custom') {
    return toEnvironmentShortName(customName);
  }
  const match = TENANT_ENVIRONMENTS.find((e) => e.value === environment);
  return match?.shortName || toEnvironmentShortName(environment);
};

export const URL_BASE_DOMAIN = 'pilvisystems.com';

/** DNS-safe service / host label segment. */
export const toDnsLabel = (raw, maxLen = 48) => String(raw || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9-]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .replace(/-{2,}/g, '-')
  .slice(0, maxLen);

/**
 * Public service URLs:
 *   {tenant}-{service}.{environment}.{software}.pilvisystems.com
 * Example (base tenant, frontend, dev, erp4c):
 *   https://base-frontend.dev.erp4c.pilvisystems.com/
 */
export const buildTenantServiceUrls = ({
  tenantName = '',
  environmentShortName = '',
  softwareShortName = '',
  services = ['frontend', 'backend']
} = {}) => {
  const env = toDnsLabel(environmentShortName, ENV_SHORT_NAME_MAX);
  const tenant = toDnsLabel(tenantName, 40);
  const software = toDnsLabel(softwareShortName, 16);
  if (!env || !tenant || !software) return [];

  return services.map((role) => {
    const serviceRole = toDnsLabel(role, 24);
    const hostLabel = `${tenant}-${serviceRole}`;
    const host = `${hostLabel}.${env}.${software}.${URL_BASE_DOMAIN}`;
    return {
      role: serviceRole,
      service: hostLabel,
      software,
      environment: env,
      tenant,
      host,
      url: `https://${host}`
    };
  });
};

/**
 * Build service URLs for one or more software applications.
 * Each selected software shortname gets frontend/backend URLs.
 */
export const buildTenantServiceUrlsForProducts = ({
  tenantName = '',
  environmentShortName = '',
  softwareProducts = [],
  services = ['frontend', 'backend']
} = {}) => {
  const products = (softwareProducts || []).filter((p) => p?.short_name);
  if (!products.length) return [];

  return products.flatMap((product) =>
    buildTenantServiceUrls({
      tenantName,
      environmentShortName,
      softwareShortName: product.short_name,
      services
    }).map((row) => ({
      ...row,
      product_name: product.name || product.short_name,
      kind: product.kind || 'application'
    }))
  );
};

/**
 * Resource names:
 *   namespace / deployment base : {software}-{tenant}
 *   workloads                 : {software}-{tenant}-{role}
 * e.g. erp4c-omegasolutions, erp4c-omegasolutions-backend
 */
export const buildCatalogResourceNames = ({
  catalogShortName = '',
  tenantName = '',
  environmentShortName = '',
  roles = ['frontend', 'backend', 'database']
} = {}) => {
  const product = toDnsLabel(catalogShortName, 16);
  const tenant = String(tenantName || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .slice(0, 40);
  const env = toDnsLabel(environmentShortName, ENV_SHORT_NAME_MAX);
  if (!product || !tenant) return [];

  const namespace = `${product}-${tenant}`.slice(0, 63);
  const digitsPreview = 'xxxxxxxx';

  return roles.map((role) => {
    const serviceRole = toDnsLabel(role, 24);
    const base = `${namespace}-${serviceRole}`.slice(0, 63);
    const urlRow = ['frontend', 'backend'].includes(serviceRole)
      ? buildTenantServiceUrls({
        tenantName,
        environmentShortName,
        softwareShortName: product,
        services: [serviceRole]
      })[0]
      : null;
    return {
      role: serviceRole,
      short_name: product,
      namespace,
      base_name: base,
      deployment_name: role === 'database' ? namespace : base,
      service_name: base,
      pod_name_prefix: `${base}-`,
      database_name: env ? `${namespace}-${env}-db` : `${namespace}-db`,
      database_user: env ? `${namespace}-${env}-db-user` : `${namespace}-db-user`,
      infra_branch_name: env ? `${tenant}-${env}-${digitsPreview}` : null,
      url: urlRow?.url || '',
      host: urlRow?.host || ''
    };
  });
};

/**
 * Deployment naming summary for Add Tenant preview panels.
 */
export const buildDeploymentNamingSummary = ({
  catalogShortName = '',
  tenantName = '',
  environmentShortName = ''
} = {}) => {
  const rows = buildCatalogResourceNames({
    catalogShortName,
    tenantName,
    environmentShortName,
    roles: ['frontend', 'backend', 'database']
  });
  if (!rows.length) return null;
  const first = rows[0];
  return {
    namespace: first.namespace,
    database_name: first.database_name,
    database_user: first.database_user,
    infra_branch_name: first.infra_branch_name,
    workloads: Object.fromEntries(rows.map((r) => [r.role, r.service_name]))
  };
};

/**
 * Expand selected catalog products × roles into naming rows for Service URLs tabs.
 */
export const buildTenantNamingPreview = ({
  catalogProducts = [],
  tenantName = '',
  environmentShortName = '',
  roles = ['frontend', 'backend', 'database']
} = {}) => {
  const products = (catalogProducts || []).filter((p) => p?.short_name);
  if (!products.length) {
    return buildCatalogResourceNames({
      catalogShortName: 'product',
      tenantName,
      environmentShortName,
      roles
    }).map((row) => ({ ...row, product_name: '', kind: '' }));
  }

  return products.flatMap((product) =>
    buildCatalogResourceNames({
      catalogShortName: product.short_name,
      tenantName,
      environmentShortName,
      roles
    }).map((row) => ({
      ...row,
      product_name: product.name || product.short_name,
      kind: product.kind || ''
    }))
  );
};
